/**
 * useCPCustomCode
 *
 * Client-only composable that watches the portal's `customCode` settings and
 * injects/removes custom CSS, JS, header tracking code, and footer tracking
 * code into the live DOM.
 *
 * Mirrors the logic in useGroupCustomCode (communities groups layer) but
 * sources its data from the location/portal settings instead of a group.
 *
 * Guards:
 * - Only runs when `isClientportal === true` (global store app-type check).
 * - Only runs on the client (`process.server` guard).
 * - Only injects when `liveMode === true`.
 *
 * Injection anchor IDs:
 *   #cp-siteCustomHeader  (place in CPRootContainer template)
 *   #cp-siteCustomFooter  (place in CPRootContainer template)
 */

import { computed, onMounted, onUnmounted, watch } from 'vue'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useLocation } from '@common/location-layer/composables/useLocation'
import type { PortalCustomCode } from '@common/location-layer/types/location'

const ATTR = 'data-cp-custom-code'

/** Serialise a customCode object to a stable string for content-equality checks. */
function serializeCustomCode(code: PortalCustomCode): string {
  return JSON.stringify({
    customJs: code.customJs ?? '',
    customCss: code.customCss ?? '',
    headerCode: code.headerCode ?? '',
    footerCode: code.footerCode ?? '',
    liveMode: code.liveMode ?? false,
  })
}

/**
 * Wrap raw JS in a try/catch so errors in customer code don't propagate to
 * our own error monitoring.
 */
function sanitizeJs(jsCode: string): string {
  // Only strip real tags (a letter right after '<' or '</', plus a closing '>') so a
  // bare '<'/'>' comparison operator (e.g. `i < N`) is never mistaken for an unclosed
  // tag. Loop to a fixed point so nested/overlapping fragments (e.g. `</scr</script>ipt>`)
  // can't strip down to a reconstructed `</script>` that would truncate the script again.
  const TAG_PATTERN = /<\/?[a-zA-Z][^<>]*>/g
  let stripped = jsCode
  let previous: string
  do {
    previous = stripped
    stripped = previous.replace(TAG_PATTERN, '')
  } while (stripped !== previous)

  return `try {
  ${stripped.trim()}
} catch (error) {
  console.error('Custom JS execution error:', error);
}`
}

/** Remove all previously injected custom-code elements. */
function removeInjectedElements(): void {
  document.querySelectorAll(`[${ATTR}]`).forEach(el => el.remove())
}

function injectCss(css: string): void {
  if (!css) return
  const style = document.createElement('style')
  style.setAttribute('type', 'text/css')
  style.setAttribute(ATTR, 'css')
  style.innerHTML = css
  document.head.appendChild(style)
}

function injectJs(js: string): void {
  if (!js) return
  const sanitized = sanitizeJs(js)

  try {
    new Function(sanitized)
  } catch (e) {
    console.error('Custom JS is invalid — skipping injection:', e)
    return
  }

  const script = document.createElement('script')
  script.setAttribute('type', 'text/javascript')
  script.setAttribute(ATTR, 'js')
  script.innerHTML = sanitized
  document.head.appendChild(script)
}

function injectHtml(targetId: string, html: string, attrValue: string): void {
  if (!html) return
  const target = document.getElementById(targetId)
  if (!target) return

  // If the value has no HTML tags, treat it as raw JavaScript and wrap it.
  const normalized = html.includes('<') ? html : `<script>${html}<\/script>`

  // Parse the HTML string and re-create elements so browser executes scripts.
  // DOMParser-parsed <script> nodes are inert — they must be re-created to execute.
  //
  // IMPORTANT: The HTML5 parser places bare <script> tags (with no preceding body
  // content) into <head>, not <body>. Walk both to handle all cases.
  const parser = new DOMParser()
  const doc = parser.parseFromString(normalized, 'text/html')

  const allNodes = [...Array.from(doc.head.childNodes), ...Array.from(doc.body.childNodes)]

  allNodes.forEach(node => {
    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement
      const script = document.createElement('script')
      script.setAttribute(ATTR, attrValue)
      if (original.src) {
        script.src = original.src
        script.async = original.async
      } else {
        script.innerHTML = original.innerHTML
      }
      Array.from(original.attributes).forEach(attr => {
        if (attr.name !== 'src') script.setAttribute(attr.name, attr.value)
      })
      target.appendChild(script)
    } else {
      const clone = (node as Element).cloneNode?.(true) as Element
      if (clone && clone.setAttribute) clone.setAttribute(ATTR, attrValue)
      if (clone) target.appendChild(clone)
    }
  })
}

function applyCustomCode(code: PortalCustomCode): void {
  removeInjectedElements()

  if (!code.liveMode) return

  injectCss(code.customCss ?? '')
  injectJs(code.customJs ?? '')
  injectHtml('cp-siteCustomHeader', code.headerCode ?? '', 'header')
  injectHtml('cp-siteCustomFooter', code.footerCode ?? '', 'footer')
}

/**
 * Call this composable once in CPRootContainer.
 * It watches the location store's customCode and re-applies whenever it changes.
 */
export function useCPCustomCode(): void {
  if (process.server) return

  const { isClientportal } = useGlobal()
  if (!isClientportal) return

  const { store: locationStore } = useLocation()

  let lastAppliedContent: string | null = null

  const currentCustomCode = computed(() => locationStore.customCode)

  function applyIfChanged(customCode: PortalCustomCode | null | undefined): void {
    const content = customCode ? serializeCustomCode(customCode) : null
    if (content === lastAppliedContent) return
    lastAppliedContent = content
    if (customCode) {
      applyCustomCode(customCode)
    } else {
      removeInjectedElements()
    }
  }

  onMounted(() => {
    applyIfChanged(currentCustomCode.value)
  })

  watch(currentCustomCode, customCode => {
    applyIfChanged(customCode)
  })

  onUnmounted(() => {
    removeInjectedElements()
  })
}
